/**
 * GroundingStudio.jsx — Fissal Grounding Contexts & Community Intelligence Studio.
 *
 * Allows administrators to import, inspect, edit, toggle, and search external
 * grounding datasets (community Discord trade commentary, trader sheet notes,
 * and market intelligence briefs) powered by SQLite FTS5 BM25.
 */
import { Show, For, createSignal, onMount } from 'solid-js';
import toast from '../lib/toastStore.js';

export default function GroundingStudio() {
  const [groundingList, setGroundingList] = createSignal([]);
  const [groundingLoading, setGroundingLoading] = createSignal(false);
  const [groundingSaving, setGroundingSaving] = createSignal(false);
  const [groundingCategory, setGroundingCategory] = createSignal('All');
  const [groundingSearch, setGroundingSearch] = createSignal('');
  const [groundingTotalTokens, setGroundingTotalTokens] = createSignal(0);

  // Live Grounding Query Tester signals
  const [groundingTestQuery, setGroundingTestQuery] = createSignal('Mournhold bid inflation and capital trader competition');
  const [groundingTestLoading, setGroundingTestLoading] = createSignal(false);
  const [groundingTestResults, setGroundingTestResults] = createSignal(null);
  const [groundingTestDuration, setGroundingTestDuration] = createSignal(0);
  const [groundingTestErr, setGroundingTestErr] = createSignal('');

  // Import Modal & Edit Drawer State
  const [showGroundingModal, setShowGroundingModal] = createSignal(false);
  const [editingGroundingId, setEditingGroundingId] = createSignal(null);
  const [gFormTitle, setGFormTitle] = createSignal('');
  const [gFormSlug, setGFormSlug] = createSignal('');
  const [gFormCategory, setGFormCategory] = createSignal('kiosks');
  const [gFormSourceType, setGFormSourceType] = createSignal('discord_export');
  const [gFormSourceRef, setGFormSourceRef] = createSignal('');
  const [gFormDescription, setGFormDescription] = createSignal('');
  const [gFormContent, setGFormContent] = createSignal('');
  const [gFormTags, setGFormTags] = createSignal('');
  const [gFormAutoParse, setGFormAutoParse] = createSignal(true);
  const [gFormError, setGFormError] = createSignal('');

  onMount(() => {
    loadGroundingContexts();
  });

  async function loadGroundingContexts() {
    setGroundingLoading(true);
    try {
      const res = await fetch('/api/admin/fissal/grounding', { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (Array.isArray(data.contexts)) {
        setGroundingList(data.contexts);
        setGroundingTotalTokens(data.total_tokens || 0);
      }
    } catch (err) {
      console.warn('Failed loading grounding contexts:', err.message);
    } finally {
      setGroundingLoading(false);
    }
  }

  async function toggleGroundingActive(id, currentActive) {
    const nextActive = !currentActive;
    try {
      const res = await fetch(`/api/admin/fissal/grounding/${id}`, {
        method: 'PUT',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: nextActive }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || 'Failed to update status');
      setGroundingList(prev => prev.map(c => c.id === id ? { ...c, is_active: nextActive } : c));
      toast.success(`Context is now ${nextActive ? 'ACTIVE' : 'DISABLED'}`);
    } catch (err) {
      toast.error(`Toggle error: ${err.message}`);
    }
  }

  async function deleteGroundingContext(id, title) {
    if (!confirm(`Are you sure you want to delete grounding context "${title}"?`)) return;
    try {
      const res = await fetch(`/api/admin/fissal/grounding/${id}`, {
        method: 'DELETE',
        credentials: 'same-origin',
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || 'Failed to delete');
      setGroundingList(prev => prev.filter(c => c.id !== id));
      toast.success(`Deleted "${title}"`);
    } catch (err) {
      toast.error(`Delete error: ${err.message}`);
    }
  }

  async function saveGroundingForm() {
    setGFormError('');
    if (!gFormTitle().trim()) {
      setGFormError('Title is required');
      return;
    }
    if (!gFormContent().trim()) {
      setGFormError('Content is required');
      return;
    }
    setGroundingSaving(true);
    try {
      const isEdit = Boolean(editingGroundingId());
      const url = isEdit ? `/api/admin/fissal/grounding/${editingGroundingId()}` : '/api/admin/fissal/grounding';
      const method = isEdit ? 'PUT' : 'POST';

      const payload = {
        title: gFormTitle().trim(),
        slug: gFormSlug().trim() || undefined,
        category: gFormCategory(),
        source_type: gFormSourceType(),
        source_ref: gFormSourceRef().trim(),
        description: gFormDescription().trim(),
        content: gFormContent().trim(),
        tags: gFormTags().trim(),
        auto_parse: gFormAutoParse(),
      };

      const res = await fetch(url, {
        method,
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || 'Failed to save context');

      toast.success(isEdit ? 'Context updated successfully!' : 'Context imported & indexed into FTS5!');
      setShowGroundingModal(false);
      loadGroundingContexts();
    } catch (err) {
      setGFormError(err.message);
    } finally {
      setGroundingSaving(false);
    }
  }

  async function runGroundingSearch() {
    const q = groundingTestQuery().trim();
    if (!q) return;
    setGroundingTestLoading(true);
    setGroundingTestErr('');
    setGroundingTestResults(null);
    try {
      const res = await fetch('/api/admin/fissal/grounding/test-search', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: q }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || 'Search failed');
      setGroundingTestResults(data.results || []);
      setGroundingTestDuration(data.duration_ms || 0);
      toast.success(`Found ${data.count} matches in ${data.duration_ms}ms`);
    } catch (err) {
      setGroundingTestErr(err.message);
    } finally {
      setGroundingTestLoading(false);
    }
  }

  return (
    <div class="v2-widget__scroll" style={{ padding: '12px' }}>
      {/* Header Bar */}
      <div style={{ display: 'flex', 'justify-content': 'space-between', 'align-items': 'center', gap: '10px', 'flex-wrap': 'wrap', 'margin-bottom': '12px' }}>
        <div>
          <div class="v2-section-h" style={{ margin: 0, display: 'flex', 'align-items': 'center', gap: '8px' }}>
            <span>📚</span>
            <span>Fissal Grounding Contexts & Community Intel</span>
          </div>
          <div class="v2-dim" style={{ 'font-size': '11px', 'margin-top': '2px' }}>
            Manage qualitative community commentary, Discord transcripts, and trader sheet notes for AI grounding.
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', 'align-items': 'center' }}>
          <span style={{
            padding: '4px 10px',
            'border-radius': '999px',
            'font-size': '11px',
            'font-weight': 600,
            background: 'rgba(251, 191, 36, 0.15)',
            color: '#fbbf24',
            border: '1px solid rgba(251, 191, 36, 0.3)',
          }}>
            ● {groundingList().filter(c => c.is_active).length} / {groundingList().length} Contexts Active
          </span>
          <span class="v2-dim" style={{ 'font-size': '11px' }}>
            ⚡ {groundingTotalTokens().toLocaleString()} Tokens
          </span>
          <button
            class="v2-btn-sm"
            style={{ background: 'var(--fissal)', color: '#000', 'font-weight': 700 }}
            onClick={() => {
              setEditingGroundingId(null);
              setGFormTitle('');
              setGFormSlug('');
              setGFormCategory('kiosks');
              setGFormSourceType('discord_export');
              setGFormSourceRef('');
              setGFormDescription('');
              setGFormContent('');
              setGFormTags('');
              setGFormAutoParse(true);
              setGFormError('');
              setShowGroundingModal(true);
            }}
          >
            Import / Add Context ➕
          </button>
          <button class="v2-btn-sm" disabled={groundingLoading()} onClick={loadGroundingContexts}>
            {groundingLoading() ? 'Refreshing…' : 'Refresh ↻'}
          </button>
        </div>
      </div>

      {/* Engine Status Banner */}
      <div style={{
        display: 'flex',
        'align-items': 'center',
        'justify-content': 'space-between',
        gap: '10px',
        padding: '8px 12px',
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.08)',
        'border-radius': '8px',
        'margin-bottom': '14px',
        'font-size': '11px',
      }}>
        <div style={{ display: 'flex', 'align-items': 'center', gap: '8px' }}>
          <span style={{ color: '#fbbf24', 'font-size': '14px' }}>⚡</span>
          <span>
            <strong>SQLite FTS5 BM25 Engine:</strong> Active. Sub-millisecond text grounding automatically enriches <code style={{ color: 'var(--fissal)' }}>get_kiosk_bid_suggestions</code> and Fissal queries.
          </span>
        </div>
        <span class="v2-dim" style={{ 'font-size': '10px' }}>0 MB RAM Overhead · Native SQLite</span>
      </div>

      {/* ── Live Grounding Query Tester ── */}
      <div style={{
        border: '1px solid rgba(251, 191, 36, 0.35)',
        background: 'linear-gradient(180deg, rgba(251, 191, 36, 0.05) 0%, rgba(20, 20, 24, 0.5) 100%)',
        'border-radius': '10px',
        padding: '14px',
        'margin-bottom': '16px',
      }}>
        <div style={{ display: 'flex', 'justify-content': 'space-between', 'align-items': 'center', 'margin-bottom': '8px' }}>
          <div style={{ 'font-weight': 700, 'font-size': '13px', color: '#fde68a', display: 'flex', 'align-items': 'center', gap: '6px' }}>
            <span>🔍</span>
            <span>Live Grounding Search & BM25 Relevance Tester</span>
          </div>
          <span style={{ 'font-size': '10px', color: 'var(--dim)' }}>Instant FTS5 Evaluation</span>
        </div>
        <div class="v2-dim" style={{ 'font-size': '11px', 'margin-bottom': '10px' }}>
          Test what community commentary, bid rumors, or trader sheet notes Fissal retrieves when asked about specific locations or market dynamics.
        </div>

        {/* Presets */}
        <div style={{ display: 'flex', gap: '6px', 'flex-wrap': 'wrap', 'margin-bottom': '10px' }}>
          <span class="v2-dim" style={{ 'font-size': '10px', 'font-weight': 600, 'align-self': 'center' }}>PRESET QUERIES:</span>
          <button
            class="v2-btn-sm"
            style={{ 'font-size': '10px' }}
            onClick={() => setGroundingTestQuery('Mournhold bid inflation and capital competition')}
          >
            🏛️ Mournhold Bids
          </button>
          <button
            class="v2-btn-sm"
            style={{ 'font-size': '10px' }}
            onClick={() => setGroundingTestQuery('Elden Root foot traffic and turnover')}
          >
            🌲 Elden Root Foot Traffic
          </button>
          <button
            class="v2-btn-sm"
            style={{ 'font-size': '10px' }}
            onClick={() => setGroundingTestQuery('Vivec City open-air crafting alchemy')}
          >
            ⚖️ Vivec City Turnover
          </button>
          <button
            class="v2-btn-sm"
            style={{ 'font-size': '10px' }}
            onClick={() => setGroundingTestQuery('Rawlkha central bazaar ergonomics loyalty')}
          >
            🏰 Rawl\'kha Loyalty
          </button>
          <button
            class="v2-btn-sm"
            style={{ 'font-size': '10px' }}
            onClick={() => setGroundingTestQuery('Outlaw Refuges low risk bids')}
          >
            🦹 Outlaw Refuges
          </button>
        </div>

        {/* Search Input Row */}
        <div style={{ display: 'flex', gap: '8px', 'align-items': 'center' }}>
          <input
            class="v2-input"
            type="text"
            style={{ flex: 1, 'font-size': '12px' }}
            value={groundingTestQuery()}
            onInput={(e) => setGroundingTestQuery(e.currentTarget.value)}
            placeholder="Search grounding contexts (e.g. Mournhold, Elden Root, luxury furnishings)..."
          />
          <button
            class="v2-btn-sm"
            style={{ background: 'rgba(251, 191, 36, 0.2)', color: '#fde68a', border: '1px solid rgba(251, 191, 36, 0.4)', 'font-weight': 700, padding: '6px 14px' }}
            disabled={groundingTestLoading() || !groundingTestQuery().trim()}
            onClick={runGroundingSearch}
          >
            {groundingTestLoading() ? 'Searching…' : 'Run Grounding Search ❯'}
          </button>
        </div>

        {/* Test Results Display */}
        <Show when={groundingTestResults()}>
          <div style={{
            'margin-top': '12px',
            padding: '12px',
            background: 'rgba(0,0,0,0.4)',
            border: '1px solid rgba(251, 191, 36, 0.25)',
            'border-radius': '8px',
            'font-size': '11px',
          }}>
            <div style={{ display: 'flex', 'justify-content': 'space-between', 'margin-bottom': '8px', 'font-weight': 700 }}>
              <span style={{ color: '#fde68a' }}>
                ✓ Matched {groundingTestResults()?.length} Context Snippet(s)
              </span>
              <span style={{ color: 'var(--t-dim, #94a3b8)' }}>
                {groundingTestDuration()}ms latency
              </span>
            </div>

            <For each={groundingTestResults()}>{(match) => {
              const safeHtml = String(match.snippet || '')
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/&lt;mark&gt;/gi, '<mark>')
                .replace(/&lt;\/mark&gt;/gi, '</mark>');
              return (
                <div style={{
                  'margin-bottom': '8px',
                  background: 'rgba(255,255,255,0.02)',
                  padding: '8px 10px',
                  'border-radius': '6px',
                  border: '1px solid rgba(255,255,255,0.06)'
                }}>
                  <div style={{ display: 'flex', 'justify-content': 'space-between', 'margin-bottom': '4px' }}>
                    <strong style={{ color: 'var(--fissal)' }}>{match.title}</strong>
                    <span class="v2-dim" style={{ 'font-size': '10px' }}>
                      Source: {match.source_type} · BM25 Score: {match.relevance_score}
                    </span>
                  </div>
                  <div style={{ lineHeight: '1.4', color: '#e2e8f0', 'font-size': '11px' }} innerHTML={safeHtml} />
                </div>
              );
            }}</For>
          </div>
        </Show>
        <Show when={groundingTestErr()}>
          <div class="v2-error" style={{ 'margin-top': '8px' }}>{groundingTestErr()}</div>
        </Show>
      </div>

      {/* ── Filters & Category Bar ── */}
      <div style={{ display: 'flex', 'justify-content': 'space-between', 'align-items': 'center', gap: '8px', 'flex-wrap': 'wrap', 'margin-bottom': '12px' }}>
        <div style={{ display: 'flex', gap: '6px', 'flex-wrap': 'wrap' }}>
          <For each={['All', 'kiosks', 'trading', 'market', 'general']}>{(cat) => (
            <button
              class={`v2-gt-chip ${groundingCategory() === cat ? 'is-on' : ''}`}
              style={{ 'font-size': '11px', 'text-transform': 'capitalize' }}
              onClick={() => setGroundingCategory(cat)}
            >
              {cat}
            </button>
          )}</For>
        </div>
        <input
          class="v2-input"
          type="text"
          style={{ width: '200px', 'font-size': '11px' }}
          value={groundingSearch()}
          onInput={(e) => setGroundingSearch(e.currentTarget.value)}
          placeholder="Filter by title, tags, or description…"
        />
      </div>

      {/* ── Grounding Cards Grid ── */}
      <div style={{
        display: 'grid',
        'grid-template-columns': 'repeat(auto-fill, minmax(340px, 1fr))',
        gap: '12px',
        'margin-bottom': '16px',
      }}>
        <For each={groundingList().filter(c => {
          if (groundingCategory() !== 'All' && c.category !== groundingCategory()) return false;
          if (groundingSearch()) {
            const s = groundingSearch().toLowerCase();
            return c.title.toLowerCase().includes(s) || (c.tags && c.tags.toLowerCase().includes(s)) || (c.description && c.description.toLowerCase().includes(s));
          }
          return true;
        })}>{(ctx) => {
          const sourceIcon = () => {
            switch (ctx.source_type) {
              case 'discord_export': return '💬';
              case 'google_sheet': return '📊';
              case 'markdown': return '📝';
              default: return '📄';
            }
          };
          return (
            <div style={{
              border: '1px solid ' + (ctx.is_active ? 'rgba(255,255,255,0.1)' : 'rgba(239, 68, 68, 0.2)'),
              background: ctx.is_active ? 'rgba(255,255,255,0.018)' : 'rgba(239, 68, 68, 0.02)',
              'border-radius': '10px',
              padding: '14px',
              display: 'flex',
              'flex-direction': 'column',
              'justify-content': 'space-between',
              opacity: ctx.is_active ? 1 : 0.65,
              transition: 'all 150ms ease',
            }}>
              <div>
                <div style={{ display: 'flex', 'justify-content': 'space-between', 'align-items': 'center', gap: '8px', 'margin-bottom': '6px' }}>
                  <div style={{ display: 'flex', 'align-items': 'center', gap: '6px' }}>
                    <span style={{ 'font-size': '15px' }}>{sourceIcon()}</span>
                    <strong>{ctx.title}</strong>
                  </div>
                  <span style={{
                    'font-size': '10px',
                    padding: '2px 6px',
                    'border-radius': '4px',
                    background: 'rgba(251, 191, 36, 0.15)',
                    color: '#fbbf24',
                    'font-weight': 600,
                    'text-transform': 'uppercase',
                  }}>
                    {ctx.category}
                  </span>
                </div>

                <div style={{ 'font-size': '10px', color: 'var(--fissal)', 'font-family': 'var(--font-mono)', 'margin-bottom': '6px' }}>
                  {ctx.slug}
                </div>

                <Show when={ctx.source_ref}>
                  <div class="v2-dim" style={{ 'font-size': '10px', 'margin-bottom': '6px' }}>
                    Source Ref: {ctx.source_ref}
                  </div>
                </Show>

                <div class="v2-dim" style={{ 'font-size': '11px', lineHeight: '1.4', 'margin-bottom': '10px' }}>
                  {ctx.description || ctx.content_preview}
                </div>

                <Show when={ctx.tags}>
                  <div style={{ display: 'flex', gap: '4px', 'flex-wrap': 'wrap', 'margin-bottom': '10px' }}>
                    <For each={ctx.tags.split(',').map(t => t.trim()).filter(Boolean)}>
                      {(tag) => (
                        <span style={{
                          'font-size': '9px',
                          background: 'rgba(255,255,255,0.06)',
                          padding: '1px 5px',
                          'border-radius': '3px',
                          color: 'var(--dim)',
                        }}>
                          #{tag}
                        </span>
                      )}
                    </For>
                  </div>
                </Show>
              </div>

              <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '10px' }}>
                <div style={{ display: 'flex', 'justify-content': 'space-between', 'align-items': 'center', 'margin-bottom': '8px', 'font-size': '10px', color: 'var(--dim)' }}>
                  <span>~{ctx.token_count?.toLocaleString()} tokens</span>
                  <span>{new Date(ctx.updated_at).toLocaleDateString()}</span>
                </div>

                <div style={{ display: 'flex', 'justify-content': 'space-between', 'align-items': 'center', gap: '6px' }}>
                  <button
                    class="v2-btn-sm"
                    style={{
                      background: ctx.is_active ? 'rgba(74, 222, 128, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                      color: ctx.is_active ? '#4ade80' : '#f87171',
                      border: '1px solid ' + (ctx.is_active ? 'rgba(74, 222, 128, 0.3)' : 'rgba(239, 68, 68, 0.3)'),
                      'font-weight': 700,
                      'font-size': '10px',
                    }}
                    onClick={() => toggleGroundingActive(ctx.id, ctx.is_active)}
                  >
                    {ctx.is_active ? '● ACTIVE' : '○ DISABLED'}
                  </button>

                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                      class="v2-btn-sm"
                      style={{ 'font-size': '10px' }}
                      onClick={async () => {
                        const res = await fetch(`/api/admin/fissal/grounding/${ctx.id}`, { credentials: 'same-origin' });
                        const data = await res.json();
                        if (data.ok && data.context) {
                          const c = data.context;
                          setEditingGroundingId(c.id);
                          setGFormTitle(c.title);
                          setGFormSlug(c.slug);
                          setGFormCategory(c.category);
                          setGFormSourceType(c.source_type);
                          setGFormSourceRef(c.source_ref || '');
                          setGFormDescription(c.description || '');
                          setGFormContent(c.content);
                          setGFormTags(c.tags || '');
                          setGFormAutoParse(false);
                          setGFormError('');
                          setShowGroundingModal(true);
                        }
                      }}
                    >
                      Inspect / Edit
                    </button>
                    <button
                      class="v2-btn-sm"
                      style={{ 'font-size': '10px', color: '#f87171' }}
                      onClick={() => deleteGroundingContext(ctx.id, ctx.title)}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        }}</For>
      </div>

      {/* ── Import / Edit Context Modal ── */}
      <Show when={showGroundingModal()}>
        <div style={{
          border: '1px solid rgba(251, 191, 36, 0.3)',
          background: 'rgba(20, 20, 24, 0.98)',
          'border-radius': '10px',
          padding: '16px',
          'margin-bottom': '16px',
        }}>
          <div style={{ display: 'flex', 'justify-content': 'space-between', 'align-items': 'center', 'margin-bottom': '12px' }}>
            <div style={{ 'font-weight': 700, 'font-size': '13px', color: '#fde68a' }}>
              {editingGroundingId() ? 'Edit Grounding Context' : 'Import / Ingest Grounding Context'}
            </div>
            <button class="v2-btn-sm" onClick={() => setShowGroundingModal(false)}>Close ✕</button>
          </div>

          <div style={{ display: 'grid', 'grid-template-columns': '1fr 1fr', gap: '10px', 'margin-bottom': '10px' }}>
            <div>
              <div class="v2-dim" style={{ 'font-size': '10px', 'margin-bottom': '3px' }}>TITLE:</div>
              <input
                class="v2-input"
                style={{ width: '100%', 'font-size': '11px' }}
                value={gFormTitle()}
                onInput={(e) => setGFormTitle(e.currentTarget.value)}
                placeholder="e.g. Community Trader Discord Discussions"
              />
            </div>
            <div>
              <div class="v2-dim" style={{ 'font-size': '10px', 'margin-bottom': '3px' }}>CATEGORY:</div>
              <select
                class="v2-input"
                style={{ width: '100%', 'font-size': '11px' }}
                value={gFormCategory()}
                onChange={(e) => setGFormCategory(e.currentTarget.value)}
              >
                <option value="kiosks">kiosks (Trader Locations & Bids)</option>
                <option value="trading">trading (Sales & Market)</option>
                <option value="market">market (Economic Trends)</option>
                <option value="general">general (Guild Lore & Rules)</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', 'grid-template-columns': '1fr 1fr', gap: '10px', 'margin-bottom': '10px' }}>
            <div>
              <div class="v2-dim" style={{ 'font-size': '10px', 'margin-bottom': '3px' }}>SOURCE TYPE:</div>
              <select
                class="v2-input"
                style={{ width: '100%', 'font-size': '11px' }}
                value={gFormSourceType()}
                onChange={(e) => setGFormSourceType(e.currentTarget.value)}
              >
                <option value="discord_export">💬 Discord Chat Export / Transcript</option>
                <option value="google_sheet">📊 Google Spreadsheet / CSV Notes</option>
                <option value="markdown">📝 Markdown Document</option>
                <option value="raw_text">📄 Plain Text Intelligence</option>
              </select>
            </div>
            <div>
              <div class="v2-dim" style={{ 'font-size': '10px', 'margin-bottom': '3px' }}>SOURCE REFERENCE / URL:</div>
              <input
                class="v2-input"
                style={{ width: '100%', 'font-size': '11px' }}
                value={gFormSourceRef()}
                onInput={(e) => setGFormSourceRef(e.currentTarget.value)}
                placeholder="e.g. Discord: #trader-chat or Google Sheets link"
              />
            </div>
          </div>

          <div style={{ 'margin-bottom': '10px' }}>
            <div class="v2-dim" style={{ 'font-size': '10px', 'margin-bottom': '3px' }}>TAGS (comma-separated):</div>
            <input
              class="v2-input"
              style={{ width: '100%', 'font-size': '11px' }}
              value={gFormTags()}
              onInput={(e) => setGFormTags(e.currentTarget.value)}
              placeholder="e.g. mournhold, elden root, bids, kiosk inflation, capital"
            />
          </div>

          <div style={{ 'margin-bottom': '10px' }}>
            <div class="v2-dim" style={{ 'font-size': '10px', 'margin-bottom': '3px' }}>DESCRIPTION / SUMMARY:</div>
            <input
              class="v2-input"
              style={{ width: '100%', 'font-size': '11px' }}
              value={gFormDescription()}
              onInput={(e) => setGFormDescription(e.currentTarget.value)}
              placeholder="Brief description of what this context contains..."
            />
          </div>

          <div style={{ 'margin-bottom': '10px' }}>
            <div style={{ display: 'flex', 'justify-content': 'space-between', 'align-items': 'center', 'margin-bottom': '3px' }}>
              <div class="v2-dim" style={{ 'font-size': '10px' }}>CONTENT (Paste raw chat, CSV, or markdown):</div>
              <div class="v2-dim" style={{ 'font-size': '10px' }}>
                ~{Math.ceil(gFormContent().length / 3.8).toLocaleString()} estimated tokens
              </div>
            </div>
            <textarea
              class="v2-input"
              style={{ width: '100%', height: '140px', 'font-family': 'var(--font-mono)', 'font-size': '11px' }}
              value={gFormContent()}
              onInput={(e) => setGFormContent(e.currentTarget.value)}
              placeholder="Paste Discord chat logs, CSV rows from Google Sheets, or market intelligence notes here..."
            />
          </div>

          <div style={{ display: 'flex', 'justify-content': 'space-between', 'align-items': 'center', 'margin-bottom': '10px' }}>
            <label style={{ display: 'flex', 'align-items': 'center', gap: '6px', 'font-size': '11px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={gFormAutoParse()}
                onChange={(e) => setGFormAutoParse(e.currentTarget.checked)}
              />
              <span>Auto-parse & normalize dialogue/rows (strips timestamps & noise)</span>
            </label>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button class="v2-btn-sm" onClick={() => setShowGroundingModal(false)}>Cancel</button>
              <button
                class="v2-btn-sm"
                style={{ background: 'var(--fissal)', color: '#000', 'font-weight': 700 }}
                disabled={groundingSaving()}
                onClick={saveGroundingForm}
              >
                {groundingSaving() ? 'Saving & Indexing…' : 'Save & Index Context'}
              </button>
            </div>
          </div>

          <Show when={gFormError()}>
            <div class="v2-error">{gFormError()}</div>
          </Show>
        </div>
      </Show>
    </div>
  );
}
